import React, { useState } from 'react';
import styled from '@emotion/styled';
import CircleLeftIcon from '../Icons/CircleLeftIcon';
import CircleRightIcon from '../Icons/CircleRightIcon';

////////////////////////////////////////////////////////////////////////////////
// Pagination Helpers
////////////////////////////////////////////////////////////////////////////////

// Special marker for ellipsis
const DOTS = 'DOTS' as const;

// Generate a range of numbers [start..end]
function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
}

/**
 * Compute pagination items (page indices and DOTS) for one-indexed UI.
 * Uses zero-based logic internally.
 * - totalPages: total number of pages (>=1)
 * - currentPage: zero-based index of current page (0..totalPages-1)
 * - siblingCount: number of page siblings to show on each side of current
 * Returns an array containing either a page index (0-based) or the DOTS marker.
 */
function getPaginationRange(
  totalPages: number,
  currentPage: number,
  siblingCount = 1
): Array<number | typeof DOTS> {
  const totalPageNumbers = siblingCount * 2 + 5; // max slots including first, last, current±siblings, and 2 DOTS

  // If we have fewer pages than the max slots, show all
  if (totalPageNumbers >= totalPages) {
    return range(0, totalPages - 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    totalPages - 2
  );

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 3;

  const firstIndex = 0;
  const lastIndex = totalPages - 1;

  // Only right ellipsis
  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = range(0, leftItemCount - 1);
    return [...leftRange, DOTS, lastIndex];
  }

  // Only left ellipsis
  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = range(totalPages - rightItemCount, totalPages - 1);
    return [firstIndex, DOTS, ...rightRange];
  }

  // Both ellipses
  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = range(leftSiblingIndex, rightSiblingIndex);
    return [firstIndex, DOTS, ...middleRange, DOTS, lastIndex];
  }

  // Fallback
  return [];
}

////////////////////////////////////////////////////////////////////////////////
// PaginationControl Component
////////////////////////////////////////////////////////////////////////////////

interface PaginationControlProps {
  endPage: number; // total pages
  defaultNumOfPagination?: number; // maintained prop name (not used internally)
  changePage: (page: number) => void; // callback on page change
  currentPage?: number; // ✅ Add this line
}

const PaginationControl: React.FC<PaginationControlProps> = ({
  endPage,
  defaultNumOfPagination = 6, // default kept but logic uses siblingCount=1
  changePage,
  currentPage,
}) => {
  // Track one-indexed active page
  const isControlled = typeof currentPage === 'number';

  const [internalPage, setInternalPage] = useState<number>(1);
  const activePage = isControlled ? currentPage! : internalPage;

  // Convert to zero-based index
  const currentIndex = activePage - 1;
  // Use siblingCount=1 to get behavior like: 1 … 4 5 6 … 20
  const paginationRange = getPaginationRange(endPage, currentIndex, 1);

  // Update local and parent state
  const handlePageChange = (page: number) => {
    if (!isControlled) {
      setInternalPage(page);
    }
    changePage(page);
  };

  return (
    <Container>
      {/* Previous Button */}
      <IconWrap
        onClick={() => handlePageChange(Math.max(1, activePage - 1))}
        className={activePage === 1 ? 'left-disable' : ''}
      >
        <CircleLeftIcon /> Prev
      </IconWrap>

      {/* Page Numbers and Ellipses */}
      {paginationRange.map((item, idx) =>
        item === DOTS ? (
          <Ellipses key={`e-${idx}`}>…</Ellipses>
        ) : (
          <NumberBox
            key={item}
            className={activePage === item + 1 ? 'active' : ''}
            onClick={() => handlePageChange(item + 1)}
          >
            {item + 1}
          </NumberBox>
        )
      )}

      {/* Next Button */}
      <IconWrap
        onClick={() => handlePageChange(Math.min(endPage, activePage + 1))}
        className={activePage === endPage ? 'right-disable' : ''}
      >
        Next <CircleRightIcon />
      </IconWrap>
    </Container>
  );
};

export default PaginationControl;

////////////////////////////////////////////////////////////////////////////////
// Styled Components
////////////////////////////////////////////////////////////////////////////////

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: fit-content;
  margin-left: auto;
  margin: auto;

  @media (max-width: 480px) {
    max-width: 22.5rem;
    margin: 0;
  }
`;

const IconWrap = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background-color: ${({ theme }) => theme.palette.mainPurple};
  margin-left: 16px;
  margin-right: 16px;
  border-radius: 4px;
  border: none;
  color: white;
  padding: 6px 16px;

  &:hover {
    cursor: pointer;
  }

  &.left-disable {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.greyGrey3};
  }

  &.right-disable {
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.greyGrey3};
  }

  @media (max-width: 480px) {
    margin-left: 6px;
    margin-right: 6px;
    padding: 3px 6px;
    font-size: 14px;
  }
`;

const NumberBox = styled.div`
  display: flex;
  font-size: 14px;
  border-bottom: 2px solid transparent;
  padding: 6px 2px;
  margin: 0 16px;
  font-weight: 700;
  cursor: pointer;

  &.active {
    border-bottom: 2px solid ${({ theme }) => theme.palette.mainPurple};
  }

  @media (max-width: 480px) {
    margin: 0 0.5rem;
  }
`;

const Ellipses = styled.span`
  font-size: 32px;
  font-weight: 700;
  margin: 2px;
  display: flex;
  color: ${({ theme }) => theme.palette.borderColor};
`;
